import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../api/apiClient';
import { deriveIdentityPhase } from '../utils/identityStatus';

export const IDENTITY_REFRESH_EVENT = 'identity-verification:refresh';

function friendlyError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (typeof error?.message === 'string' && error.message) return error.message;
  return fallback;
}

function safeHostedVerificationUrl(rawUrl) {
  let parsed;
  try {
    parsed = new URL(String(rawUrl || ''));
  } catch {
    throw new Error('The verification service returned an invalid link.');
  }

  if (parsed.protocol !== 'https:') {
    throw new Error('The verification service returned an unsafe link.');
  }

  return parsed.toString();
}

function dispatchRefresh() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(IDENTITY_REFRESH_EVENT));
  }
}

export default function useIdentityVerification({
  enabled = true,
  userId = null,
} = {}) {
  const [dobStatus, setDobStatus] = useState(null);
  const [identityStatus, setIdentityStatus] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [savingDob, setSavingDob] = useState(false);
  const [starting, setStarting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!enabled) {
      setLoading(false);
      return null;
    }

    if (!silent) setLoading(true);
    setError('');

    try {
      const [dobResponse, identityResponse] = await Promise.all([
        apiRequest.get('/api/account/date-of-birth/status'),
        apiRequest.get('/api/identity/status'),
      ]);

      const nextDob = dobResponse?.data || null;
      const nextIdentity = identityResponse?.data || null;
      setDobStatus(nextDob);
      setIdentityStatus(nextIdentity);

      return {
        dobStatus: nextDob,
        identityStatus: nextIdentity,
      };
    } catch (requestError) {
      setError(friendlyError(
        requestError,
        'We could not load your verification status. Please try again.',
      ));
      return null;
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setDobStatus(null);
      setIdentityStatus(null);
      setLoading(false);
      return undefined;
    }

    refresh();

    const handleRefresh = () => {
      refresh({ silent: true });
    };

    window.addEventListener(IDENTITY_REFRESH_EVENT, handleRefresh);
    return () => {
      window.removeEventListener(IDENTITY_REFRESH_EVENT, handleRefresh);
    };
  }, [enabled, userId, refresh]);

  const saveDateOfBirth = useCallback(async (dateOfBirth) => {
    const value = String(dateOfBirth || '').trim();

    if (!/^[0-9]{2}-[0-9]{2}-[0-9]{4}$/.test(value)) {
      const message = 'Enter your date of birth as DD-MM-YYYY.';
      setError(message);
      throw new Error(message);
    }

    setSavingDob(true);
    setError('');

    try {
      const response = await apiRequest.put('/api/account/date-of-birth', {
        date_of_birth: value,
      });

      setDobStatus({
        date_of_birth_set: true,
        source: 'canonical',
        age_band: response?.data?.age_band || 'unknown',
        guardian_required: Boolean(response?.data?.guardian_required),
        identity_verification_eligible: Boolean(
          response?.data?.identity_verification_eligible,
        ),
        didit_verification_blocked_reason:
          response?.data?.didit_verification_blocked_reason || null,
      });

      await refresh({ silent: true });
      dispatchRefresh();
      return response?.data || {};
    } catch (requestError) {
      const message = friendlyError(
        requestError,
        'We could not save your date of birth. Please try again.',
      );
      setError(message);
      throw new Error(message);
    } finally {
      setSavingDob(false);
    }
  }, [refresh]);

  const startVerification = useCallback(async () => {
    setStarting(true);
    setError('');

    try {
      const response = await apiRequest.post('/api/identity/start', {});
      const data = response?.data || {};

      if (
        data.identity_verified ||
        data.adult_verified ||
        ['approved', 'verified'].includes(String(data.status || '').toLowerCase())
      ) {
        setIdentityStatus(data);
        dispatchRefresh();
        return { ...data, alreadyApproved: true };
      }

      if (!data.verification_url) {
        await refresh({ silent: true });

        if (
          ['pending', 'in_progress', 'awaiting_user'].includes(
            String(data.status || '').toLowerCase(),
          )
        ) {
          return data;
        }

        throw new Error('The verification service did not return a secure link.');
      }

      const hostedUrl = safeHostedVerificationUrl(data.verification_url);

      // The hosted URL is intentionally not persisted in localStorage,
      // sessionStorage or application state.
      window.location.assign(hostedUrl);
      return data;
    } catch (requestError) {
      const message = friendlyError(
        requestError,
        'We could not start identity verification. Please try again.',
      );
      setError(message);
      throw new Error(message);
    } finally {
      setStarting(false);
    }
  }, [refresh]);

  const checkStatus = useCallback(async () => {
    setChecking(true);
    setError('');

    try {
      // Reconciliation is authenticated and reads the session ID only from
      // the server-side linked record. The browser never supplies a session ID.
      const response = await apiRequest.post('/api/identity/reconcile', {});
      if (response?.data) {
        setIdentityStatus(response.data);
      }
      await refresh({ silent: true });
      dispatchRefresh();
      return response?.data || {};
    } catch (requestError) {
      const message = friendlyError(
        requestError,
        'We could not refresh the verification result. Please try again.',
      );
      setError(message);
      throw new Error(message);
    } finally {
      setChecking(false);
    }
  }, [refresh]);

  const phase = useMemo(
    () => deriveIdentityPhase({
      dobStatus,
      identityStatus,
      loading,
      error,
    }),
    [dobStatus, identityStatus, loading, error],
  );

  return {
    dobStatus,
    identityStatus,
    phase,
    loading,
    savingDob,
    starting,
    checking,
    error,
    clearError: () => setError(''),
    refresh,
    saveDateOfBirth,
    startVerification,
    checkStatus,
  };
}
